import React, { useEffect, useState, useRef } from 'react';
import Axios from '../../api/axios';
import DataGrid, { Paging, Editing, Column, LoadPanel, Scrolling, Lookup, Toolbar as GridToolbar, Item as GridToolbarItem } from '../BootstrapDataGrid';
import notify from '../../utils/notifyUtil';
import { FileUploader, Box, BoxItem as Item, Button as SingleButton, RadioGroup } from '../BootstrapControls';
import ResimIzlePanel from './ResimIzlePanel';
import Dict from '../../messages.js';
import * as glb from '../../globals.js';
import { CARI_MEDIA_ACCEPT, getByDownloadRef, getMediaKindFromPath, isOfficePath, isPdfPath, isVideoPath } from '../../utils/mediaCache.js';
import { getCached, setCached, clearCached, carRefCacheKey } from '../../utils/requestCache.js';
import { gridHeightByRowCount, gridMaxHeightForRows } from '../../utils/gridAutoHeight.js';
import Form, { GroupItem, SimpleItem, Label } from '../BootstrapForm';
import { canDeleteByOwner, FOREIGN_DELETE_MESSAGE, resimIsYapanRef } from '../../utils/ownRecordDelete.js';

const YER_LOOKUP_IDS = new Set([1, 2, 3, 4, 5]);
const ZAMAN_LOOKUP_IDS = new Set(['O', 'C', 'S']);

/** Lookup sütunları ile uyumlu tiplere çevirir (API string / legacy değerler). */
function normalizeResimRow(row) {
    if (!row || typeof row !== 'object') return row;
    const r = { ...row };

    if (r.CR_YER != null && r.CR_YER !== '') {
        const n = Number(r.CR_YER);
        if (Number.isFinite(n) && YER_LOOKUP_IDS.has(n)) r.CR_YER = n;
    } else {
        r.CR_YER = null;
    }

    if (r.CR_ZAMAN != null && r.CR_ZAMAN !== '') {
        const z = String(r.CR_ZAMAN).trim().toUpperCase();
        if (z === 'ÖNCE' || z === 'ONCE') r.CR_ZAMAN = 'O';
        else if (z === 'EKİM' || z === 'EKIM') r.CR_ZAMAN = 'C';
        else if (z === 'SONRA') r.CR_ZAMAN = 'S';
        else if (ZAMAN_LOOKUP_IDS.has(z.charAt(0))) r.CR_ZAMAN = z.charAt(0);
        else r.CR_ZAMAN = null;
    } else {
        r.CR_ZAMAN = null;
    }

    if (r.CR_SAB_REF != null && r.CR_SAB_REF !== '') {
        const sab = Number(r.CR_SAB_REF);
        r.CR_SAB_REF = Number.isFinite(sab) && sab > 0 ? sab : null;
    } else {
        r.CR_SAB_REF = null;
    }

    return r;
}

function normalizeResimList(list) {
    return Array.isArray(list) ? list.map(normalizeResimRow) : [];
}

function filterResimMetaList(list, { carRef, casRef, filterType }) {
    const rows = normalizeResimList(list);
    if (!carRef || carRef <= 0) return [];
    if (filterType === 'sale' && casRef) {
        return rows.filter((r) => Number(r.CR_CAS_REF) === Number(casRef));
    }
    if (filterType === 'general') {
        return rows.filter((r) => !r.CR_CAS_REF);
    }
    if (filterType === 'both' && casRef) {
        return rows.filter((r) => !r.CR_CAS_REF || Number(r.CR_CAS_REF) === Number(casRef));
    }
    return rows;
}

export default function ResimPanel({ carRef, casRef, resimReadOnly, carAdSoy, filterType: initialFilterType, gridVisibleRowCount = null, gridHeight = null }) {
        const resimListCacheKey = () => carRefCacheKey('CAR_RES_LIST', carRef, `${casRef || 0}_${filterType}`);
        const fetchList = (forceRefresh = false) => {
            if (!carRef || carRef <= 0) {
                setResimList([]);
                return;
            }
            const key = resimListCacheKey();
            if (!forceRefresh) {
                const cached = getCached(key);
                if (cached && Array.isArray(cached)) {
                    setResimList(cached);
                    return;
                }
                const metaCached = getCached(carRefCacheKey('RESIM_META', carRef));
                if (metaCached && Array.isArray(metaCached)) {
                    const filtered = filterResimMetaList(metaCached, { carRef, casRef, filterType });
                    setCached(key, filtered);
                    setResimList(filtered);
                    return;
                }
            }
            let url = `${process.env.REACT_APP_BACKEND_SERVER}fileops/carResList?CR_CAR_REF=${carRef}&filterType=${filterType}`;
            if (casRef && casRef > 0) url += `&CR_CAS_REF=${casRef}`;
            url += `&cid=${glb.default.KULLANICI.KUL_DEF_FIR_REF}`;
            Axios.get(url)
                .then(res => {
                    const list = normalizeResimList(res.data || []);
                    setCached(key, list);
                    setResimList(list);
                })
                .catch(err => { console.log(err); setResimList([]); });
        };
    const [resimList, setResimList] = useState([]);
    const [activeResim, setActiveResim] = useState({ CR_YER: null, CR_ZAMAN: null, CR_SAB_REF: null, CR_ACK: '' });
    const [activeMedia, setActiveMedia] = useState({ url: '', kind: 'image' });
    const [hasPendingGridChanges, setHasPendingGridChanges] = useState(false);

    function getMediaKind(path) {
        const kind = getMediaKindFromPath(path);
        return kind === 'other' ? 'image' : kind;
    }

    function officeFileLabel(path) {
        const name = path ? String(path).split(/[\\/]/).pop() : '';
        const ext = name.includes('.') ? name.split('.').pop().toUpperCase() : 'DOSYA';
        return { name: name || 'Ofis belgesi', ext };
    }
    const gridRef = useRef(null);
    const [lightboxVisible, setLightboxVisible] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    // Satış varsa 'sale', yoksa 'general' başlangıç değeri
    const [filterType, setFilterType] = useState(initialFilterType || (casRef && casRef > 0 ? 'sale' : 'general'));
    const [viewMode, setViewMode] = useState('summary'); // 'summary', 'detail'
    const [sablonOptions, setSablonOptions] = useState([]); // Şablon lookup seçenekleri
    const [activeResimDirty, setActiveResimDirty] = useState(false);


    useEffect(() => {
        fetchList();
    }, [carRef, casRef, filterType]);

    useEffect(() => {
        const onSync = (e) => {
            if (Number(e.detail?.carRef) === Number(carRef)) {
                clearCached(resimListCacheKey());
                clearCached(carRefCacheKey('RESIM_META', carRef));
                fetchList(true);
            }
        };
        window.addEventListener('cari-sync-updated', onSync);
        return () => window.removeEventListener('cari-sync-updated', onSync);
    }, [carRef, casRef, filterType]);

    // Şablon listesi: standart şablonlar + cari dökümanlar (CR_SAB_REF lookup)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!carRef || carRef <= 0) { setSablonOptions([]); return; }
            const key = carRefCacheKey('SABLON_LOOKUP', carRef);
            const cached = getCached(key);
            if (cached && Array.isArray(cached)) {
                setSablonOptions(cached);
                return;
            }
            try {
                const base = `${process.env.REACT_APP_BACKEND_SERVER}sablonops/`;
                const [stdRes, cariRes] = await Promise.all([
                    Axios.get(`${base}getSablonList`),
                    Axios.get(`${base}getSablonCariList?CAR_REF=${carRef}`),
                ]);
                const stdList = Array.isArray(stdRes.data) ? stdRes.data : [];
                const cariList = Array.isArray(cariRes.data) ? cariRes.data : [];
                const byRef = new Map();
                stdList.forEach((s) => {
                    if (s?.SAB_REF) byRef.set(s.SAB_REF, { id: s.SAB_REF, value: s.SAB_ADI });
                });
                cariList.forEach((s) => {
                    if (s?.SAB_REF) byRef.set(s.SAB_REF, { id: s.SAB_REF, value: `${s.SAB_ADI} (cari)` });
                });
                const merged = Array.from(byRef.values()).sort((a, b) => String(a.value).localeCompare(String(b.value), 'tr'));
                setCached(key, merged);
                if (!cancelled) setSablonOptions(merged);
            } catch (e) {
                if (!cancelled) setSablonOptions([]);
            }
        })();
        return () => { cancelled = true; };
    }, [carRef]);

    const onUploaded = () => {
        clearCached(resimListCacheKey());
        setHasPendingGridChanges(false);
        fetchList(true);
    };

    // Tekli silme için onay
    const deleteImage = async (cr_ref) => {
        const ok = window.confirm('Bu resmi silmek istediğinize emin misiniz?');
        if (!ok) return;
        try {
            await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/resimsil`, { CR_REF: cr_ref, kulid: glb.default.KULLANICI.KUL_REF });
            clearCached(resimListCacheKey());
            fetchList(true);
        } catch (err) {
            notify(err?.response?.data?.message || FOREIGN_DELETE_MESSAGE, 'error', 2500);
            fetchList(true);
        }
    };

    // Tümünü sil fonksiyonu
    const deleteAllImages = async () => {
        const owned = (resimList || []).filter((resim) => canDeleteByOwner(glb.default?.KULLANICI, resimIsYapanRef(resim)));
        if (owned.length === 0) {
            notify(FOREIGN_DELETE_MESSAGE, 'warning', 2500);
            return;
        }
        const ok = window.confirm(owned.length === resimList.length
            ? 'Tüm resimleri silmek istediğinize emin misiniz?'
            : 'Yalnız kendi yüklediğiniz resimler silinecek. Devam edilsin mi?');
        if (!ok) return;
        try {
            for (const resim of owned) {
                await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/resimsil`, { CR_REF: resim.CR_REF, kulid: glb.default.KULLANICI.KUL_REF });
            }
            clearCached(resimListCacheKey());
            fetchList(true);
            notify('Tüm resimler silindi.', 'success', 2000);
        } catch (err) {
            notify('Silme işlemi sırasında hata oluştu.', 'error', 2000);
        }
    };

    const openLightbox = (index = 0) => {
        if (!resimList || resimList.length === 0) return;
        setLightboxIndex(index);
        setLightboxVisible(true);
    };

    const closeLightbox = () => {
        setLightboxVisible(false);
    };

    const downloadFile = async (crRef, crResim) => {
        try {
            const folderName = `${carRef || 'cari'}_${carAdSoy || 'resimler'}`.replace(/[<>:"/\\|?*]/g, '_');
            const fileName = crResim ? crResim.split(/[\\/]/).pop() : `resim_${crRef}`;
            
            const response = await Axios.get(
                `${process.env.REACT_APP_BACKEND_SERVER}fileops/download?CR_REF=${crRef}`,
                { responseType: 'blob' }
            );
            
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.log('Download error:', err);
        }
    };

    const downloadAll = async () => {
        if (!resimList || resimList.length === 0) return;
        for (const resim of resimList) {
            await downloadFile(resim.CR_REF, resim.CR_RESIM);
            await new Promise(resolve => setTimeout(resolve, 300));
        }
    };

    // Resimler artık CAR_REF ile kaydedilebilir (CAS_REF null olabilir)
    const isNewUnsavedRecord = !carRef || carRef === -1 || carRef <= 0;

    const resolvedGridHeight = React.useMemo(() => {
        const maxFromProp = Number(gridHeight) > 0
            ? Number(gridHeight)
            : (Number(gridVisibleRowCount) > 0
                ? gridMaxHeightForRows(gridVisibleRowCount)
                : null);
        if (maxFromProp == null) return null;
        return gridHeightByRowCount((resimList || []).length, maxFromProp);
    }, [gridHeight, gridVisibleRowCount, resimList]);
    
    const filterOptions = [
        { value: 'sale', text: 'Satış İlişkili Resimler', disabled: !casRef || casRef <= 0 },
        { value: 'general', text: 'Carinin Genel Resimleri' },
        { value: 'both', text: 'Satış İlişkili ve Genel Resimler', disabled: !casRef || casRef <= 0 },
        { value: 'all', text: 'Tüm Resimler' }
    ];

    // Aktif satırın medyasını göster: video ise stream endpoint, resim ise cache
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!activeResim || !activeResim.CR_REF) { setActiveMedia({ url: '', kind: 'image' }); return; }
            const pathHint = activeResim.CR_RESIM || '';
            const kind = getMediaKind(pathHint);
            if (kind === 'video') {
                const streamUrl = `${process.env.REACT_APP_BACKEND_SERVER}fileops/stream?path=${encodeURIComponent(pathHint)}`;
                if (!cancelled) setActiveMedia({ url: streamUrl, kind: 'video' });
                return;
            }
            if (kind === 'pdf') {
                const pdfUrl = `${process.env.REACT_APP_BACKEND_SERVER}fileops/download?CR_REF=${activeResim.CR_REF}`;
                if (!cancelled) setActiveMedia({ url: pdfUrl, kind: 'pdf' });
                return;
            }
            if (kind === 'office') {
                if (!cancelled) setActiveMedia({ url: '', kind: 'office', path: pathHint });
                return;
            }
            try {
                const media = await getByDownloadRef(process.env.REACT_APP_BACKEND_SERVER, activeResim.CR_REF, pathHint);
                if (!cancelled) setActiveMedia({ url: media.url, kind: 'image' });
            } catch (e) {
                if (!cancelled) setActiveMedia({ url: '', kind: 'image' });
            }
        })();
        return () => { cancelled = true; };
    }, [activeResim]);

    const saveActiveResim = async () => {
        if (!activeResim?.CR_REF || !activeResimDirty) return;
        const crYer = activeResim.CR_YER != null && activeResim.CR_YER !== '' ? Number(activeResim.CR_YER) : null;
        const crZaman = activeResim.CR_ZAMAN != null && activeResim.CR_ZAMAN !== '' ? String(activeResim.CR_ZAMAN).trim() : null;
        try {
            const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/resimkaydet`, {
                ...activeResim,
                CR_ACK: activeResim.CR_ACK || '',
                CR_YER: Number.isFinite(crYer) ? crYer : null,
                CR_ZAMAN: crZaman,
                CR_SAB_REF: activeResim.CR_SAB_REF || null,
                CR_UPD_KUL: glb.default.KULLANICI.KUL_REF,
                CR_UPD_TAR: new Date().toISOString(),
            });
            const saved = normalizeResimRow(res?.data?.saved || activeResim);
            setActiveResim(saved);
            setResimList((prev) => prev.map((row) => row.CR_REF === saved.CR_REF ? { ...row, ...saved } : row));
            setActiveResimDirty(false);
            clearCached(resimListCacheKey());
            notify('Resim bilgileri kaydedildi.', 'success', 1800);
        } catch (err) {
            notify('Resim bilgileri kaydedilemedi.', 'error', 2500);
        }
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{
                marginBottom: 6,
                fontWeight: 'bold',
                fontSize: '14px',
                color: 'var(--dx-color-text, #212529)',
                borderBottom: '2px solid var(--dx-color-primary, #1976d2)',
                paddingBottom: 3,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
            }}>
                <span>🖼️</span>
                <span>{Dict.get('R e s i m l e r')}</span>
            </div>
            {isNewUnsavedRecord && (
                <div style={{ padding: 8, margin: '8px 0', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 4, color: '#856404' }}>
                    ℹ️ Resim eklemek için önce cari kaydını kaydedin.
                </div>
            )}       
            

            <Box direction="row" width="100%" style={{ marginBottom: 8 }}>
                <Item ratio={1}>
                    <div className={'form-avatar'} style={{ width: '100%', minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 4 }}>
                        {activeMedia.kind === 'video' ? (
                            <video src={activeMedia.url} controls style={{ width: '100%', height: '100%', objectFit: 'contain', background: 'black' }} />
                        ) : activeMedia.kind === 'pdf' ? (
                            <iframe src={activeMedia.url} title="PDF Preview" style={{ width: '100%', height: '100%', border: 'none', background: 'white' }} sandbox="allow-scripts allow-same-origin" />
                        ) : activeMedia.kind === 'office' ? (
                            (() => {
                                const { name, ext } = officeFileLabel(activeMedia.path || activeResim?.CR_RESIM);
                                return (
                                    <div style={{ textAlign: 'center', padding: 16, color: '#424242' }}>
                                        <div style={{ fontSize: 40, marginBottom: 8 }} aria-hidden>📑</div>
                                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{ext}</div>
                                        <div style={{ fontSize: 12, marginBottom: 10, maxWidth: 260, wordBreak: 'break-all' }}>{name}</div>
                                        <div style={{ fontSize: 12, color: '#757575', marginBottom: 10 }}>Önizleme desteklenmiyor</div>
                                        {activeResim?.CR_REF ? (
                                            <SingleButton
                                                text="İndir"
                                                icon="download"
                                                type="default"
                                                stylingMode="contained"
                                                height={30}
                                                onClick={() => downloadFile(activeResim.CR_REF, activeResim.CR_RESIM)}
                                            />
                                        ) : null}
                                    </div>
                                );
                            })()
                        ) : (
                            <img src={activeMedia.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        )}
                    </div>
                </Item>
                <Item ratio={1}>
                    {/* Filtre Radio Butonları */}
                    <div style={{ marginBottom: 8, padding: 8, background: 'var(--dx-color-bg-secondary, #f8f9fa)', borderRadius: 4 }}>
                        <div style={{ marginBottom: 6, fontWeight: 'bold', fontSize: 12 }}>Filtre:</div>
                        <RadioGroup
                            items={filterOptions}
                            value={filterType}
                            onValueChanged={(e) => setFilterType(e.value)}
                            layout="vertical"
                            displayExpr="text"
                            valueExpr="value"
                            itemRender={(item) => (
                                <div style={{ opacity: item.disabled ? 0.5 : 1, pointerEvents: item.disabled ? 'none' : 'auto' }}>
                                    {item.text}
                                </div>
                            )}
                        />
                    </div>
                    
                    {/* Görünüm Modu */}
                    <div style={{ padding: 8, background: 'var(--dx-color-bg-secondary, #f8f9fa)', borderRadius: 4 }}>
                        <div style={{ marginBottom: 6, fontWeight: 'bold', fontSize: 12 }}>Görünüm:</div>
                        <RadioGroup
                            items={[
                                { value: 'summary', text: 'Özet' },
                                { value: 'detail', text: 'Detay' }
                            ]}
                            value={viewMode}
                            onValueChanged={(e) => setViewMode(e.value)}
                            layout="horizontal"
                            displayExpr="text"
                            valueExpr="value"
                        />
                    </div>
                </Item>
            </Box>
            {/* Dosya seçimi + görünüm mümkün olduğunca yukarıda, boşluklar kompakt */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 1, flexWrap: 'wrap', alignItems: 'stretch' }}>
                <div style={{ flex: '1 1 auto', minWidth: 220 }}>
                    <FileUploader
                        uploadUrl={`${process.env.REACT_APP_BACKEND_SERVER}fileops/upload`}
                        uploadCustomData={{ 
                            klasor: 'cari', 
                            zaman: activeResim?.CR_ZAMAN || '',
                            yer: activeResim?.CR_YER || '',
                            aciklama: activeResim?.CR_ACK || '',
                            cr_sab_ref: activeResim?.CR_SAB_REF || '',
                            carref: carRef || -1, 
                            casref: (filterType === 'general' ? null : (casRef || null)), 
                            KUL_REF: glb.default.KULLANICI.KUL_REF 
                        }}
                        multiple={true}
                        showFileList={false}
                        onUploaded={onUploaded}
                        accept={CARI_MEDIA_ACCEPT}
                        disabled={isNewUnsavedRecord}
                    />
                </div>
                {/* İndir ve Büyük Resim butonları grid toolbar'da */}
            </div>
            {activeResim ? (
                <div style={{ marginBottom: 8, padding: 10, border: '1px solid #9aa4ae', borderRadius: 6, background: 'var(--dx-color-bg-secondary, #f8f9fa)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <strong>{activeResim?.CR_REF ? 'Aktif Resim Bilgileri' : 'Yeni Resim Bilgileri'}</strong>
                        {activeResim?.CR_REF ? <SingleButton text="Bilgileri Kaydet" icon="save" type="success" disabled={!activeResimDirty || resimReadOnly || !canDeleteByOwner(glb.default?.KULLANICI, resimIsYapanRef(activeResim))} onClick={saveActiveResim} /> : <span style={{ fontSize: 12, opacity: 0.75 }}>Bilgiler dosya yüklenirken kaydedilir</span>}
                    </div>
                    <Form formData={activeResim} readOnly={resimReadOnly || (Number(activeResim?.CR_REF) > 0 && !canDeleteByOwner(glb.default?.KULLANICI, resimIsYapanRef(activeResim)))} colCount={1} onFieldDataChanged={(e) => {
                        setActiveResim((prev) => ({ ...prev, [e.dataField]: e.value }));
                        setActiveResimDirty(true);
                    }}>
                        <GroupItem colCount={3}>
                            <SimpleItem dataField="CR_YER" editorType="dxSelectBox" editorOptions={{ items: [{ id: 1, value: 'YÜZ' }, { id: 2, value: 'YAN' }, { id: 3, value: 'ARKA' }, { id: 4, value: 'PLAN' }, { id: 5, value: 'DONÖR' }], displayExpr: 'value', valueExpr: 'id' }}><Label text={Dict.get('Yer')} /></SimpleItem>
                            <SimpleItem dataField="CR_ZAMAN" editorType="dxSelectBox" editorOptions={{ items: [{ id: 'O', value: 'Önce' }, { id: 'C', value: 'Ekim' }, { id: 'S', value: 'Sonra' }], displayExpr: 'value', valueExpr: 'id' }}><Label text={Dict.get('Zaman')} /></SimpleItem>
                            <SimpleItem dataField="CR_SAB_REF" editorType="dxSelectBox" editorOptions={{ items: sablonOptions, displayExpr: 'value', valueExpr: 'id', searchEnabled: true, showClearButton: true }}><Label text={Dict.get('Şablon')} /></SimpleItem>
                        </GroupItem>
                        <SimpleItem dataField="CR_ACK" editorType="dxTextArea" editorOptions={{ height: 140 }}><Label location="top" text={Dict.get('Açıklama')} /></SimpleItem>
                    </Form>
                </div>
            ) : null}
            <div style={{ flex: 1, minHeight: 0 }}>
            <DataGrid
                key={`resim-grid-${casRef}-${resimList.length}`}
                dataSource={resimList}
                showBorders={true}
                keyExpr="CR_REF"
                focusedRowEnabled={true}
                hoverStateEnabled={false}
                repaintChangesOnly={false}
                ref={gridRef}
                height={resolvedGridHeight || '100%'}
                width={'100%'}
                columnAutoWidth={true}
                allowColumnResizing={true}
                onFocusedRowChanged={(e) => { const active = e.row?.data || { CR_YER: null, CR_ZAMAN: null, CR_SAB_REF: null, CR_ACK: '' }; setActiveResim(active); setActiveResimDirty(false); }}
                onRowRemoved={(e) => deleteImage(e.data.CR_REF)}
                onOptionChanged={(e) => {
                    // Batch edit bekleyen değişiklik var mı? (toolbar Kaydet aktifliği)
                    if (e?.fullName === 'editing.changes') {
                        const v = e?.value;
                        setHasPendingGridChanges(Array.isArray(v) && v.length > 0);
                    }
                }}
                onSaving={async (e) => {
                    e.cancel = true;
                    if (!e.changes || e.changes.length === 0) {
                        setHasPendingGridChanges(false);
                        return;
                    }
                    const removes = e.changes.filter(c => c.type === 'remove');
                    const updates = e.changes.filter(c => c.type === 'update');
                    // Silinecek kayıt varsa onay sor
                    if (removes.length > 0) {
                        const ok = window.confirm('Seçili resimleri silmek istediğinize emin misiniz?');
                        if (!ok) return;
                    }
                    try {
                        // Silmeler
                        for (const r of removes) {
                            await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/resimsil`, { CR_REF: r.key, kulid: glb.default.KULLANICI.KUL_REF });
                        }
                        // Güncellemeler
                        for (const u of updates) {
                            const row = (resimList || []).find(r => r.CR_REF === u.key) || {};
                            const data = { ...row, ...u.data };
                            const crYer = data.CR_YER != null && data.CR_YER !== '' ? Number(data.CR_YER) : null;
                            const crZaman = data.CR_ZAMAN != null && data.CR_ZAMAN !== '' ? String(data.CR_ZAMAN).trim() : null;
                            const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/resimkaydet`, {
                                CR_REF: data.CR_REF,
                                CR_REF2: data.CR_REF,
                                CR_RESIM: data.CR_RESIM,
                                CR_ACK: data.CR_ACK || '',
                                CR_YER: Number.isFinite(crYer) ? crYer : null,
                                CR_ZAMAN: crZaman,
                                CR_SAB_REF: data.CR_SAB_REF || null,
                                CR_SIL: data.CR_SIL || 0,
                                CR_UPD_KUL: glb.default.KULLANICI.KUL_REF,
                                CR_UPD_TAR: new Date().toISOString(),
                            });
                            const saved = res?.data?.saved;
                            if (saved?.CR_REF) {
                                const normalized = normalizeResimRow(saved);
                                setResimList((prev) => prev.map((r) => (r.CR_REF === normalized.CR_REF ? { ...r, ...normalized } : r)));
                            }
                        }
                        gridRef.current?.instance?.cancelEditData();
                        setHasPendingGridChanges(false);
                        clearCached(resimListCacheKey());
                        fetchList(true);
                    } catch (err) {
                        console.log(err);
                        notify('Kaydetme sırasında hata oluştu.', 'error', 2500);
                    }
                }}
            >
                <Paging enabled={false} />
                <Editing mode="batch" allowUpdating={false} allowAdding={false} allowDeleting={(row) => canDeleteByOwner(glb.default?.KULLANICI, resimIsYapanRef(row))} />
                <Scrolling mode="standard" useNative={false} showScrollbar="always" columnRenderingMode="standard" />
                <LoadPanel enabled={true} />
                <GridToolbar>
                    <GridToolbarItem
                        location="before"
                        render={() => {
                            const disabledList = !resimList || resimList.length === 0;
                            return (
                                <div style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 8,
                                    padding: '2px 0'
                                }}>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <SingleButton
                                            text="Tümünü Sil"
                                            icon="trash"
                                            type="danger"
                                            stylingMode="contained"
                                            disabled={disabledList}
                                            height={30}
                                            onClick={deleteAllImages}
                                        />
                                        <SingleButton
                                            text="Tümünü İndir"
                                            icon="download"
                                            type="success"
                                            stylingMode="contained"
                                            disabled={disabledList}
                                            height={30}
                                            onClick={downloadAll}
                                        />
                                        <SingleButton
                                            text="Büyük Resim"
                                            icon="photo"
                                            type="default"
                                            stylingMode="contained"
                                            disabled={disabledList}
                                            height={30}
                                            onClick={() => openLightbox(0)}
                                        />
                                    </div>

                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <SingleButton
                                            icon="save"
                                            text="Kaydet"
                                            hint="Kaydet"
                                            height={30}
                                            stylingMode="contained"
                                            type="default"
                                            disabled={!hasPendingGridChanges}
                                            onClick={() => gridRef.current?.instance?.saveEditData()}
                                        />
                                        <SingleButton
                                            text="Yenile"
                                            hint="Yenile"
                                            height={30}
                                            stylingMode="outlined"
                                            type="default"
                                            onClick={() => {
                                                gridRef.current?.instance?.cancelEditData();
                                                clearCached(resimListCacheKey());
                                                setHasPendingGridChanges(false);
                                                fetchList(true);
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        }}
                    />
                </GridToolbar>
                <Column dataField="CR_REF" visible={false} caption={Dict.get('id')}></Column>
                <Column caption={Dict.get('Tip')} width={viewMode === 'detail' ? 120 : 80} allowEditing={false} cellRender={({ data }) => {
                    const p = data?.CR_RESIM || '';
                    const isVid = isVideoPath(p);
                    const isPdf = isPdfPath(p);
                    const isOffice = isOfficePath(p);
                    const officeExt = isOffice ? (p.split('.').pop() || '').toUpperCase() : '';
                    if (viewMode === 'detail') {
                        if (isVid) {
                            const streamUrl = `${process.env.REACT_APP_BACKEND_SERVER}fileops/stream?path=${encodeURIComponent(p)}`;
                            return (
                                <div style={{ width: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', position: 'relative' }}>
                                    <video src={streamUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    <span style={{ position: 'absolute', bottom: 2, right: 2, background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '2px 4px', fontSize: 10, borderRadius: 2 }}>🎬</span>
                                </div>
                            );
                        } else if (isPdf) {
                            return (
                                <div style={{ width: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
                                    <span role="img" aria-label="PDF" style={{ fontSize: 32, color: '#d32f2f' }}>📄</span>
                                </div>
                            );
                        } else if (isOffice) {
                            return (
                                <div style={{ width: 100, height: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#e8f0fe', color: '#1565c0' }}>
                                    <span role="img" aria-label="Ofis belgesi" style={{ fontSize: 28 }}>📑</span>
                                    <span style={{ fontSize: 10, fontWeight: 600 }}>{officeExt}</span>
                                </div>
                            );
                        } else {
                            return (
                                <div style={{ width: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
                                    <img 
                                        src={`${process.env.REACT_APP_BACKEND_SERVER}fileops/download?CR_REF=${data.CR_REF}`} 
                                        alt="" 
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            );
                        }
                    } else {
                        if (isVid) return <span title="Video">🎬</span>;
                        if (isPdf) return <span title="PDF" style={{ color: '#d32f2f' }}>📄</span>;
                        if (isOffice) return <span title={`Ofis (${officeExt})`} style={{ color: '#1565c0' }}>📑</span>;
                        return <span title="Resim">🖼️</span>;
                    }
                }} />
                <Column dataField="CR_YER" width={'75px'} caption={Dict.get('Yer')} >
                    <Lookup
                        dataSource={[{ id: 1, value: 'YÜZ' }, { id: 2, value: 'YAN' }, { id: 3, value: 'ARKA' },
                        { id: 4, value: 'PLAN' }, { id: 5, value: 'DONÖR' }]}
                        searchEnabled={true} searchMode='contains' displayExpr='value' searchExpr='value' valueExpr='id'
                    />
                </Column>
                <Column dataField="CR_ZAMAN"  width={'75px'}caption={Dict.get('Zaman')}>
                    <Lookup
                        dataSource={[{ id: 'O', value: 'Önce' }, { id: 'C', value: 'Ekim' }, { id: 'S', value: 'Sonra' }]}
                        searchEnabled={true} searchMode='contains' displayExpr='value' searchExpr='value' valueExpr='id'
                    />
                </Column>
                <Column dataField="CR_ACK"  width={'75px'} caption={Dict.get('Açıklama')} />
                <Column dataField="CR_SAB_REF"  width={'75px'} caption={Dict.get('Şablon')} >
                    <Lookup
                        dataSource={sablonOptions}
                        searchEnabled={true}
                        searchMode='contains'
                        displayExpr='value'
                        searchExpr='value'
                        valueExpr='id'
                    />
                </Column>
                <Column dataField="CR_GIR_KUL_ADI" caption={Dict.get('Yükleyen')} allowEditing={false} width={75} />
                <Column caption={Dict.get('İndir')}  width={'50px'} allowEditing={false} cellRender={({ data }) => (
                    <SingleButton
                        icon="download"
                        hint="İndir"
                        onClick={() => downloadFile(data.CR_REF, data.CR_RESIM)}
                    />
                )} />
                <Column dataField="CR_GIR_TAR"  width={'120px'} enabled={false} dataType="date" format="dd-MM-yyyy HH:mm" caption={Dict.get('Tarihi')} />
            </DataGrid>
            </div>

            {/* Resim İzleme Paneli */}
            <ResimIzlePanel
                resimList={resimList}
                visible={lightboxVisible}
                onClose={closeLightbox}
                initialIndex={lightboxIndex}
            />
        </div>
    );
}
